import * as React from 'react';
import {
    Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AI_LABELS, getAiAgents, getAiTasks } from '../../Api';
import { AI_STATUSES, AiAgentSummaryDto, AiExportRequest, AiPage, AiStatus, AiTaskSummaryDto } from '../../models/dtos/AiDto';
import {
    AiActionError, AiErrorAlert, aiLoadErrorText, AiDataClassChip, AiStatusChip, EnumSelect, formatAiCount, formatAiDate,
    formatAiUsd,
} from '../../components/ai/AiShared';
import { exportAiBundleToFile } from '../../components/ai/AiBundleExport';
import AiImportDialog from '../../components/ai/AiImportDialog';

type Kind = 'task' | 'agent';
type Summary = AiTaskSummaryDto | AiAgentSummaryDto;

/** Shared list for tasks and agents: search, owner service, status; server paging; export / import of bundles. */
function AiCatalogList({ kind }: { kind: Kind }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialOwner = searchParams.get('ownerService') ?? '';
    const [q, setQ] = React.useState('');
    const [ownerService, setOwnerService] = React.useState(initialOwner);
    const [status, setStatus] = React.useState<AiStatus | ''>('');
    const [debounced, setDebounced] = React.useState({ q: '', ownerService: initialOwner });
    const [rows, setRows] = React.useState<Summary[]>([]);
    const [rowCount, setRowCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: 25 });
    /** Codes (row id = code); kept across pages and filters. */
    const [selection, setSelection] = React.useState<GridRowSelectionModel>([]);
    const [includeDependencies, setIncludeDependencies] = React.useState(true);
    const [exporting, setExporting] = React.useState(false);
    const [actionError, setActionError] = React.useState<AiActionError | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [importOpen, setImportOpen] = React.useState(false);

    React.useEffect(() => {
        const timer = window.setTimeout(() => setDebounced({ q: q.trim(), ownerService: ownerService.trim() }), 400);
        return () => window.clearTimeout(timer);
    }, [q, ownerService]);

    React.useEffect(() => { setPaginationModel(p => (p.page === 0 ? p : { ...p, page: 0 })); }, [debounced, status]);

    const load = React.useCallback(async () => {
        setLoading(true);
        const filter = { q: debounced.q, ownerService: debounced.ownerService, status: status || undefined };
        const offset = paginationModel.page * paginationModel.pageSize;
        const page: AiPage<Summary> | null = kind === 'task'
            ? await getAiTasks(filter, offset, paginationModel.pageSize)
            : await getAiAgents(filter, offset, paginationModel.pageSize);
        setLoading(false);
        if (page == null) {
            setError(aiLoadErrorText(kind === 'task' ? AI_LABELS.tasks : AI_LABELS.agents, `Failed to load ${kind}s.`));
            setRows([]);
            setRowCount(0);
            return;
        }
        setError(null);
        setRows(page.items ?? []);
        setRowCount(page.totalItems ?? 0);
    }, [kind, debounced, status, paginationModel]);

    React.useEffect(() => { load(); }, [load]);

    const selectedCodes = selection.map(String);
    const exportBundle = async () => {
        const request: AiExportRequest = selectedCodes.length === 0
            ? { all: true }
            : kind === 'task' ? { tasks: selectedCodes } : { agents: selectedCodes, includeDependencies };
        setExporting(true);
        setActionError(null);
        setNotice(null);
        const outcome = await exportAiBundleToFile(request);
        setExporting(false);
        setActionError(outcome.error);
        setNotice(outcome.notice);
    };

    const columns: GridColDef<Summary>[] = [
        {
            field: 'code', headerName: 'Code', width: 230, sortable: false,
            renderCell: ({ row }) => <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{row.code}</Typography>,
        },
        {
            field: 'description', headerName: 'Description', flex: 1, minWidth: 220, sortable: false,
            renderCell: ({ row }) => <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>{row.description || '—'}</Typography>,
        },
        ...(kind === 'agent' ? [{ field: 'kind', headerName: 'Kind', width: 120, sortable: false } as GridColDef<Summary>] : []),
        { field: 'ownerService', headerName: 'Owner', width: 150, sortable: false, valueFormatter: ({ value }) => value || '—' },
        { field: 'dataClass', headerName: 'Data class', width: 110, sortable: false, renderCell: ({ row }) => <AiDataClassChip dataClass={row.dataClass} /> },
        { field: 'modelKey', headerName: 'Model', width: 170, sortable: false, valueFormatter: ({ value }) => value || '—' },
        { field: 'status', headerName: 'Status', width: 110, sortable: false, renderCell: ({ row }) => <AiStatusChip status={row.status} /> },
        {
            field: 'activity', headerName: kind === 'task' ? 'Calls 7d' : 'Runs 7d', width: 120, sortable: false,
            renderCell: ({ row }) => {
                const total = 'calls7d' in row ? row.calls7d : row.runs7d;
                const failed = 'calls7d' in row ? row.errors7d : row.failedRuns7d;
                return (
                    <Stack>
                        <Typography variant="body2">{formatAiCount(total)}</Typography>
                        {failed > 0 && <Typography variant="caption" color="error">{formatAiCount(failed)} {kind === 'task' ? 'errors' : 'failed'}</Typography>}
                    </Stack>
                );
            },
        },
        { field: 'costUsd7d', headerName: 'Cost 7d', width: 110, sortable: false, valueFormatter: ({ value }) => formatAiUsd(value as number) },
        { field: 'updatedAt', headerName: 'Updated', width: 160, sortable: false, valueFormatter: ({ value }) => formatAiDate(value as string) },
    ];

    const base = kind === 'task' ? '/ai/tasks' : '/ai/agents';
    return (
        <Stack gap={2} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4">AI {kind === 'task' ? 'tasks' : 'agents'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {kind === 'task'
                            ? 'Single-shot prompts that services execute by code (system prompt + user template with {{variables}}).'
                            : 'Tool-using agents: MCP servers, subagents and tasks they may call, run by services or autonomously.'}
                    </Typography>
                </Box>
                <Stack direction="row" gap={1} alignItems="center">
                    <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={() => setImportOpen(true)}>
                        Import
                    </Button>
                    <Tooltip title={selectedCodes.length === 0
                        ? 'Nothing selected: downloads every task and agent. Tick rows to export only those.'
                        : `Downloads the ${selectedCodes.length} selected ${kind}${selectedCodes.length === 1 ? '' : 's'} as a bundle file.`}>
                        <span>
                            <Button variant="outlined" disabled={exporting} onClick={exportBundle}
                                startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}>
                                {selectedCodes.length === 0 ? 'Export all' : `Export selected (${selectedCodes.length})`}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`${base}/new`)}>
                        New {kind}
                    </Button>
                </Stack>
            </Stack>
            <Stack direction="row" gap={1.5} flexWrap="wrap">
                <TextField size="small" label="Search" value={q} onChange={e => setQ(e.target.value)} sx={{ minWidth: 240 }} />
                <TextField size="small" label="Owner service" value={ownerService} onChange={e => setOwnerService(e.target.value)} />
                <EnumSelect size="small" label="Status" value={status} options={AI_STATUSES} allowEmpty fullWidth={false}
                    sx={{ minWidth: 160 }} onChange={setStatus} />
                <Box sx={{ flex: 1 }} />
                {kind === 'agent' && (
                    <Tooltip title={selectedCodes.length === 0
                        ? 'Export all already contains every task and agent.'
                        : 'Selected agents bring their linked tasks and subagents (recursively), so the file imports on its own.'}>
                        <FormControlLabel label="Include dependencies" disabled={selectedCodes.length === 0}
                            control={<Checkbox size="small" checked={includeDependencies} onChange={e => setIncludeDependencies(e.target.checked)} />} />
                    </Tooltip>
                )}
                {selectedCodes.length > 0 && (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <Typography variant="body2" color="text.secondary">{selectedCodes.length} selected</Typography>
                        <Button size="small" onClick={() => setSelection([])}>Clear</Button>
                    </Stack>
                )}
            </Stack>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            <AiErrorAlert error={actionError} onClose={() => setActionError(null)} />
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                getRowId={row => row.code}
                getRowHeight={() => 'auto'}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                loading={loading}
                checkboxSelection
                keepNonExistentRowsSelected
                rowSelectionModel={selection}
                onRowSelectionModelChange={setSelection}
                disableRowSelectionOnClick
                disableColumnMenu
                onRowClick={({ row }) => navigate(`${base}/${encodeURIComponent((row as Summary).code)}`)}
                sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '& .MuiDataGrid-cell': { py: 1 } }}
                localeText={{ noRowsLabel: `No ${kind}s` }}
            />
            <AiImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={() => load()} />
        </Stack>
    );
}

export default function AiTasksPage() {
    return <AiCatalogList kind="task" />;
}

export function AiAgentsPage() {
    return <AiCatalogList kind="agent" />;
}
